import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Chip,
  Avatar,
  Grid,
  LinearProgress,
} from '@mui/material';
import { leaveAdminAPI } from '../services/api';

interface EmployeeBalance {
  employee_id: number;
  nome: string;
  cognome: string;
  matricola: string;
  codice_fiscale: string;
  dipartimento: string;
  ente: string;
  ferie_residue: number;
  permessi_residue: number;
  ex_festivita_ore: number;
}

const LeaveBalanceOverview: React.FC = () => {
  const [balances, setBalances] = useState<EmployeeBalance[]>([]);
  const [filteredBalances, setFilteredBalances] = useState<EmployeeBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'ferie' | 'permessi'>('ferie');
  const [filterEnte, setFilterEnte] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadBalances();
  }, [year]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [balances, sortBy, filterEnte]);

  const loadBalances = async () => {
    setLoading(true);
    try {
      const data = await leaveAdminAPI.getBalancesOverview(year, filterEnte);
      setBalances(data.balances || []);
    } catch (error) {
      console.error('Errore nel caricamento dei saldi:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...balances];

    // Filtra per ente
    if (filterEnte) {
      filtered = filtered.filter((b) => b.ente === filterEnte);
    }

    // Ordina
    filtered.sort((a, b) => {
      if (sortBy === 'ferie') {
        return a.ferie_residue - b.ferie_residue;
      }
      return a.permessi_residue - b.permessi_residue;
    });

    setFilteredBalances(filtered);
  };

  const enti = Array.from(new Set(balances.map((b) => b.ente))).filter((e) => e);

  const getChipColor = (
    value: number,
    type: 'ferie' | 'permessi'
  ): 'error' | 'warning' | 'success' => {
    if (value < 0) return 'error';
    if (type === 'ferie' && value < 5) return 'warning';
    if (type === 'permessi' && value < 8) return 'warning';
    return 'success';
  };

  const ferieNegative = filteredBalances.filter((b) => b.ferie_residue < 0).length;
  const permessiNegativi = filteredBalances.filter((b) => b.permessi_residue < 0).length;

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Saldi Ferie e Permessi - Dipendenti visibili
        </Typography>

        <Box display="flex" gap={2}>
          <TextField
            label="Anno"
            type="number"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            size="small"
            sx={{ width: 100 }}
          />
          <TextField
            select
            label="Filtra per Ente"
            value={filterEnte}
            onChange={(e) => setFilterEnte(e.target.value)}
            size="small"
            sx={{ width: 200 }}
          >
            <MenuItem value="">Tutti</MenuItem>
            {enti.map((ente) => (
              <MenuItem key={ente} value={ente}>
                {ente}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Ordina per"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'ferie' | 'permessi')}
            size="small"
            sx={{ width: 180 }}
          >
            <MenuItem value="ferie">Ferie Residue</MenuItem>
            <MenuItem value="permessi">Permessi Residui</MenuItem>
          </TextField>
        </Box>
      </Box>

      {/* Statistiche Riepilogo */}
      <Box sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Totale Dipendenti
                </Typography>
                <Typography variant="h6">{filteredBalances.length}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Ferie negative
                </Typography>
                <Typography variant="h6" color="error.main">
                  {ferieNegative}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Permessi negativi
                </Typography>
                <Typography variant="h6" color="error.main">
                  {permessiNegativi}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#E3F2FD' }}>
              <TableCell sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                Dipendente
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                Matricola
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#0d47a1' }}>Ente</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                Ferie Residue
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                Permessi Residui
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                Ex Festività
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBalances.map((emp) => (
              <TableRow key={emp.employee_id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ mr: 2, bgcolor: '#1976d2' }}>
                      {emp.nome.charAt(0)}
                      {emp.cognome.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {emp.nome} {emp.cognome}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {emp.codice_fiscale}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{emp.matricola}</TableCell>
                <TableCell>
                  <Chip
                    label={emp.ente || 'N/A'}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={`${Math.round(emp.ferie_residue)} gg`}
                    color={getChipColor(emp.ferie_residue, 'ferie')}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={`${Math.round(emp.permessi_residue)} h`}
                    color={getChipColor(emp.permessi_residue, 'permessi')}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {Math.round(emp.ex_festivita_ore)} h
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredBalances.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Nessun saldo disponibile per i filtri selezionati.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LeaveBalanceOverview;
